import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";

import { ForgotpasswordBaseComponent } from "@mypxplat/xplat/features";
import { environment, UserService, WindowService } from "@mypxplat/xplat/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { takeUntil } from "rxjs/operators";
import { firstValueFrom } from "rxjs";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { Amplify } from "aws-amplify";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-forgotpassword",
  templateUrl: "forgotpassword.component.html",
  standalone: false,
})
export class ForgotpasswordComponent extends ForgotpasswordBaseComponent implements OnInit, OnDestroy {
  @Input() resetPasswordWithEmail: string;
  @Input() switchToPreSonusPool: boolean = false;
  @Input() switchToFenderPool: boolean = false;
  public passwordVisible: boolean = false;
  // switchPools() mutates the GLOBAL Amplify config; capture the app's config so we can restore it
  // when this modal closes (otherwise web-fender is left pointed at the PreSonus pool afterward).
  private _priorAmplifyConfig: any = null;
  constructor(
    public userService: UserService,
    public activeModal: NgbActiveModal,
    windowService: WindowService,
    public translateService: TranslateService
  ) {
    super(userService, windowService, translateService);
  }

  ngOnInit() {
    if (this.resetPasswordWithEmail) this.email = this.resetPasswordWithEmail;
    if (this.switchToPreSonusPool) this.switchPools();
    if (this.switchToFenderPool) this.switchPools(true);
    this.done.pipe(takeUntil(this.destroy$)).subscribe((isDone) => {
      if (isDone) {
        this.activeModal.close();
      }
    });
  }

  resolved(captchaResponse) {
    this.captcha = captchaResponse;
  }

  ngOnDestroy() {
    // Restore the app's original Amplify config so later auth runs against the right pool.
    if (this._priorAmplifyConfig) {
      try {
        Amplify.configure(this._priorAmplifyConfig);
      } catch {
        /* best-effort restore */
      }
    }
    super.ngOnDestroy();
  }

  switchPools(fender?) {
    if (!this._priorAmplifyConfig) {
      try {
        this._priorAmplifyConfig = Amplify.getConfig();
      } catch {
        this._priorAmplifyConfig = null;
      }
    }
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolClientId: fender ? environment.fenderCognito.clientId : environment.cognito.clientId,
          userPoolId: fender ? environment.fenderCognito.userPoolId : environment.cognito.userPoolId,
          loginWith: {
            oauth: {
              providers: ["Google", "Facebook", "Apple"],
              domain: fender ? environment.fenderCognito.domain : environment.cognito.domain,
              scopes: ["email", "profile", "openid", "aws.cognito.signin.user.admin"],
              redirectSignIn: [environment.connectUrl + "home"],
              redirectSignOut: [environment.connectUrl + "home"],
              responseType: "code",
            },
          },
        },
      },
    });
  }

  sendCode() {
    if (this.email) {
      // we are now using the new endpoint for resetting passwords which will send the code. but its not yet deployed in prod as of 4/14/26
      if (environment.features.userFenderForgotPasswordEndpoint && this.switchToFenderPool) {
        this.userService.forgotPassword(this.email).subscribe({
          next: () => {
            this.emailSent = true;
          },
          error: (err) => {
            // 404 means the user isn't in the Fender pool. They may still exist only in the legacy
            // DB, so reset against the PreSonus pool: its UserMigration_ForgotPassword trigger
            // (paeapi migrate-paeuser) creates them from the DB and Cognito sends the code. The
            // confirm step then runs against the PreSonus pool too, and the next sign-in migrates
            // them to Fender via the PreSonus fallback in connect-auth.
            if (err?.status === 404) {
              // Sign-in lowercases the email, so the migrated Cognito username has to match it.
              // submit() reuses this.email for the confirm step.
              this.email = this.email.toLowerCase().trim();
              this.switchPools();
              this.sendPoolResetCode();
            } else {
              this.emailSent = true;
            }
          },
        });
      } else {
        this.sendPoolResetCode();
      }
    }
  }

  private async sendPoolResetCode() {
    try {
      await resetPassword({ username: this.email });
    } catch (err: any) {
      // A confirmed-but-unverified PreSonus Cognito user can't receive a reset code yet ("no
      // registered/verified email"). Ask paeapi to verify the email (only when it matches a real
      // Customer), then retry once so the code actually sends. The code (to that mailbox) is the
      // ownership proof; this grants nothing on its own.
      if (err?.name === "InvalidParameterException") {
        try {
          await firstValueFrom(this.userService.preparePresonusReset(this.email));
          await resetPassword({ username: this.email });
        } catch {
          /* fall through — still show success below so we don't reveal account existence */
        }
      }
    }
    // Always show success, so the form doesn't reveal which emails have accounts.
    this.emailSent = true;
  }

  submit() {
    if (this.newPassword == this.newPassword2 && this.checkPassword()) {
      this.loading = true;

      confirmResetPassword({
        username: this.email,
        confirmationCode: this.code,
        newPassword: this.newPassword,
      })
        .then((result) => {
          this.success = true;
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          if (err.code == "LimitExceededException") {
            alert("You have exceeded your tries in resetting your password. Please try again later.");
          } else if (err.code == "CodeMismatchException") {
            alert("The code you entered is invalid.");
          } else {
            alert(err.message);
          }
        });
    } else {
      alert("Passwords do not match or your new password is not strong enough.");
    }
  }

  checkPassword() {
    this.passwordPasses = this.userService.passwordTester.test(this.newPassword);
    return this.passwordPasses;
  }
}
